import { bytesToHex } from "./hex"
import { EthernaSdkError } from "@/classes"

declare global {
  interface ExternalProvider {
    _state?: {
      accounts: string[] | null
      initialized: boolean
      isConnected: boolean
      isPermanentlyDisconnected: boolean
      isUnlocked: boolean
    }
    isMetaMask?: boolean
    autoRefreshOnNetworkChange?: boolean
    chainId?: string
    networkVersion?: string
    selectedAddress?: string
    on?(event: "accountsChanged", callback: (accounts: string[]) => void): void
    on?(event: "chainChanged", callback: (chainId: string) => void): void
    on?(event: "connect", callback: (connectInfo: any) => void): void
    on?(event: "disconnect", callback: (error: ProviderRpcError) => void): void
    removeListener?(event: string, callback: Function): void
    isConnected?(): Promise<boolean>
    enable?(): Promise<string[]>
    sendAsync?: (
      request: { method: string; params?: Array<any> },
      callback: (error: any, response: any) => void,
    ) => void
    send?: (
      request: { method: string; params?: Array<any> },
      callback: (error: any, response: any) => void,
    ) => void
    request?: (request: { method: string; params?: Array<any> }) => Promise<any>
  }

  interface ProviderRpcError extends Error {
    message: string
    code: number
    data?: unknown
  }

  interface Window {
    // ethereum
    ethereum?: ExternalProvider
  }
}

/**
 * Sign a message with the user wallet
 *
 * @param digest Hex string of the message
 * @param address Signing address
 * @returns Signed hash
 */
export const signMessage = async (
  digest: string | Uint8Array,
  address: string,
): Promise<string> => {
  if (!window.ethereum)
    throw new EthernaSdkError(
      "MISSING_WALLET",
      "No wallet installed. Try installing MetaMask extension.",
    )
  if (!window.ethereum.request)
    throw new EthernaSdkError(
      "OUTDATED_WALLET",
      "You have an old version of your wallet. Try to update MetaMask.",
    )

  const accounts = await window.ethereum.request({ method: "eth_requestAccounts" })

  if (!accounts || !accounts.length)
    throw new EthernaSdkError("LOCKED_WALLET", "Unlock your wallet.")

  const hexDigest = digest instanceof Uint8Array ? bytesToHex(digest) : digest

  const data = await window.ethereum.request({
    method: "personal_sign",
    params: [address, hexDigest],
  })
  return data
}

/**
 * Covert an ETH address to bytes
 *
 * @param address Full address string (eg: 0xab...)
 * @returns The address bytes
 */
export const addressBytes = (address: string) => {
  const bytes = address.replace(/^0x/, "").match(/.{1,2}/g) ?? []
  return new Uint8Array(bytes.map((b) => parseInt(b, 16)))
}

/**
 * Check if a string a valid eth address
 *
 * @param address Address string value
 */
export const checkIsEthAddress = (address: string | null | undefined) => {
  const isEthereumAddress = /^(0x)?[0-9a-f]{40}$/i.test(address || "")
  return isEthereumAddress
}

/**
 * Get the shorten string of a address
 *
 * @param address Address string value
 */
export const shortenEthAddr = (address: string | null | undefined) => {
  if (!address) return ""
  if (address.length <= 5) return address
  return `${address.substring(0, 5)}...${address.substring(address.length - 5, address.length)}`
}

/**
 * Check if a provider is injected by the browser
 *
 * @param provider Web3 provider
 */
export const checkUsingInjectedProvider = (provider: any) => {
  const { isFortmatic, isPortis, isWalletConnect, isSquarelink, isAuthereum } = provider

  if (isFortmatic || isPortis || isWalletConnect || isSquarelink || isAuthereum) return false
  return true
}

/**
 * Fetch the wallet accounts
 */
export const fetchAccounts = async () => {
  if (!window.ethereum || !window.ethereum.request) return []
  return await window.ethereum.request({ method: "eth_requestAccounts" })
}

/**
 * Switch the wallet account
 */
export const switchAccount = async (address: string) => {
  if (!window.ethereum || !window.ethereum.request) return
  return await window.ethereum.request({
    method: "wallet_requestPermissions",
    params: [
      {
        eth_accounts: { [address]: true },
      },
    ],
  })
}

/**
 * Check if the wallet is locked
 */
export const checkWalletLocked = () => {
  if (!window.ethereum || !window.ethereum.request) return false
  if (window.ethereum._state?.accounts?.length) return false
  if (window.ethereum.selectedAddress) return false
  return true
}

/**
 * Get the network name from the id
 *
 * @param networkId Id of the networks
 */
export const getNetworkName = (networkId: number | string | undefined) => {
  if (!networkId) return ""

  switch (+networkId) {
    case 1:
      return "Main"
    case 2:
      return "Morder"
    case 3:
      return "Ropsten"
    case 4:
      return "Rinkeby"
    case 42:
      return "Kovan"
    default:
      return "Unknown"
  }
}
